(function (root, factory) {
  const dependencies =
    typeof module === 'object' && module.exports
      ? {
          analytics: require('./ai-chat-analytics.js'),
          api: require('./ai-chat-api.js'),
          controls: require('./ai-chat-controls.js'),
          dealerPricing: require('./ai-chat-dealer-pricing.js'),
          conversation: require('./ai-chat-conversation.js'),
          hubSpotForm: require('./ai-chat-hubspot-form.js'),
          lifecycle: require('./ai-chat-widget-lifecycle.js'),
          messages: require('./ai-chat-messages.js'),
          navigation: require('./ai-chat-navigation.js'),
          pricingFlow: require('./ai-chat-pricing-flow.js'),
          quote: require('./ai-chat-quote.js'),
          state: require('./ai-chat-state.js'),
        }
      : {
          analytics: root && root.MarineMaxAIChatAnalytics,
          api: root && root.MarineMaxAIChatApi,
          controls: root && root.MarineMaxAIChatControls,
          dealerPricing: root && root.MarineMaxAIChatDealerPricing,
          conversation: root && root.MarineMaxAIChatConversation,
          hubSpotForm: root && root.MarineMaxAIChatHubSpotForm,
          lifecycle: root && root.MarineMaxAIChatWidgetLifecycle,
          messages: root && root.MarineMaxAIChatMessages,
          navigation: root && root.MarineMaxAIChatNavigation,
          pricingFlow: root && root.MarineMaxAIChatPricingFlow,
          quote: root && root.MarineMaxAIChatQuote,
          state: root && root.MarineMaxAIChatState,
        };
  const appModule = factory(dependencies, root);

  if (typeof module === 'object' && module.exports) {
    module.exports = appModule;
  }

  if (root) {
    root.MarineMaxAIChatApp = appModule;
  }
})(typeof window !== 'undefined' ? window : null, function (dependencies, root) {
  'use strict';

  const chatRootSelector = '[data-ai-chat-instance]';
  const bootstrapRegistry = new WeakMap();
  const instanceRegistry = new WeakMap();

  function requireFactory(moduleRef, factoryName) {
    if (!moduleRef || typeof moduleRef[factoryName] !== 'function') {
      throw new Error(`MarineMax AI Chat dependency is missing ${factoryName}`);
    }
    return moduleRef[factoryName];
  }

  function normalizeStockNumber(value) {
    return String(value || '')
      .trim()
      .replace(/^#+/, '');
  }

  function resolvePromptType(trigger) {
    if (!trigger) return 'suggestion_prompt';
    const action = String(trigger.dataset.aiChatAction || '');
    if (action === 'hubspot' || trigger.classList.contains('expert-chat-trigger')) {
      return 'contact_prompt';
    }
    if (action === 'pricing') return 'pricing_prompt';
    if (
      action === 'focus-input' ||
      trigger.classList.contains('ai-chat-mobile-cta__button--chat')
    ) {
      return 'freeform_prompt';
    }
    return 'suggestion_prompt';
  }

  function createInitializationErrorHandler(windowRef, onError) {
    if (typeof onError === 'function') return onError;
    return (error, context) => {
      const consoleRef = windowRef && windowRef.console;
      if (!consoleRef || typeof consoleRef.error !== 'function') return;
      consoleRef.error('MarineMax AI Chat failed to initialize', context, error);
    };
  }

  function createChatApp(options = {}) {
    const chatRoot = options.root;
    const windowRef = options.window || root;
    const modules = options.modules || dependencies;
    if (!chatRoot || !windowRef) {
      throw new Error('AI Chat app requires a root and window');
    }

    const createChatClient = requireFactory(modules.api, 'createChatClient');
    const createHubSpotClient = requireFactory(modules.api, 'createHubSpotClient');
    const createInventoryIqClient = requireFactory(
      modules.api,
      'createInventoryIqClient',
    );
    const createAnalytics = requireFactory(modules.analytics, 'createAnalytics');
    const createMessageController = requireFactory(
      modules.messages,
      'createMessageController',
    );
    const createConversationController = requireFactory(
      modules.conversation,
      'createConversationController',
    );
    const createControlsController = requireFactory(
      modules.controls,
      'createControlsController',
    );
    const createNavigationController = requireFactory(
      modules.navigation,
      'createNavigationController',
    );
    const createHubSpotFormController = requireFactory(
      modules.hubSpotForm,
      'createHubSpotFormController',
    );
    const createQuoteController = requireFactory(
      modules.quote,
      'createQuoteController',
    );
    const createPricingFlowController = requireFactory(
      modules.pricingFlow,
      'createPricingFlowController',
    );
    const createWidgetLifecycle = requireFactory(
      modules.lifecycle,
      'createWidgetLifecycle',
    );
    const createStateManager = requireFactory(modules.state, 'createStateManager');
    const messageFormats = modules.state.messageFormats;
    const messageTypes = modules.state.messageTypes;
    const views = modules.state.views;
    const requestFrame =
      typeof windowRef.requestAnimationFrame === 'function'
        ? windowRef.requestAnimationFrame.bind(windowRef)
        : (callback) => callback();

    const chatBox = chatRoot.querySelector('.ai-chat__chat');
    const input = chatRoot.querySelector('.ai-chat__input textarea');
    const sendButton = chatRoot.querySelector('.ai-chat__send-btn');
    const viewQuoteButton = chatRoot.querySelector('.ai-chat__view-quote-btn');
    const suggestionContainer = chatRoot.querySelector('.ai-chat__suggest');
    const backButton = chatRoot.querySelector('.ai-chat__back');
    const defaultInputPlaceholder =
      String(chatRoot.dataset.inputPlaceholder || '').trim() ||
      'Type your question';
    const productTitle = String(chatRoot.dataset.productTitle || '').trim();
    const enableGa4Context = chatRoot.dataset.enableGa4Context === 'true';
    const boatBrand = String(
      options.boatBrand ?? chatRoot.dataset.boatBrand ?? '',
    ).trim();
    const boatModel = String(
      options.boatModel ?? chatRoot.dataset.boatModel ?? '',
    ).trim();
    const stockNumber = normalizeStockNumber(
      options.stockNumber ?? chatRoot.dataset.stockNumber,
    );
    const storeCode = String(
      options.storeCode ?? chatRoot.dataset.inventoryStoreCode ?? '',
    ).trim();

    let analyticsClient = null;
    let controlsController = null;
    let conversationController = null;
    let hubSpotFormController = null;
    let navigationController = null;
    let pricingFlowController = null;
    let dealerPricingController = null;
    let quoteController = null;
    let widgetLifecycleController = null;
    let backButtonBound = false;
    let destroyed = false;

    function resizeMainInputTextarea(resizeOptions = {}) {
      if (!input) return;
      const inputShell = input.closest('.ai-chat__input');
      const minHeight = 29;
      const maxHeight = 108;

      if (resizeOptions.reset) input.value = '';
      input.style.height = 'auto';
      const nextHeight = Math.min(
        maxHeight,
        Math.max(minHeight, input.scrollHeight || minHeight),
      );
      input.style.height = `${nextHeight}px`;
      input.style.overflowY =
        (input.scrollHeight || 0) > maxHeight ? 'auto' : 'hidden';
      if (inputShell) {
        inputShell.classList.toggle('is-multiline', nextHeight > 31);
      }
    }

    function captureStructuredViewData(view) {
      if (dealerPricingController && dealerPricingController.ownsView(view)) {
        return dealerPricingController.captureViewData();
      }
      if (quoteController && quoteController.ownsView(view)) {
        return quoteController.captureViewData();
      }
      return pricingFlowController
        ? pricingFlowController.captureViewData(view)
        : null;
    }

    function restoreStructuredView(snapshot) {
      if (dealerPricingController && dealerPricingController.ownsView(snapshot.view)) {
        return dealerPricingController.restoreView(snapshot);
      }
      if (quoteController && quoteController.ownsView(snapshot.view)) {
        return quoteController.restoreView(snapshot);
      }
      return pricingFlowController
        ? pricingFlowController.restoreView(snapshot)
        : false;
    }

    function resolveRestoredContinueDisabled(
      snapshot,
      restoredStructuredView,
      fallback,
    ) {
      if (
        !restoredStructuredView ||
        !pricingFlowController ||
        !pricingFlowController.ownsView(snapshot.view)
      ) {
        return fallback;
      }
      return pricingFlowController.resolveContinueDisabled(snapshot, fallback);
    }

    function resolveSuggestionMessage(trigger) {
      return String(
        trigger?.dataset?.aiPromptMessage || trigger?.textContent || '',
      ).trim();
    }

    function pushPromptInteraction(trigger) {
      if (!trigger || !analyticsClient) return false;
      const payload = {
        chat_tool: 'boatchat',
        action: 'prompt_clicked',
        prompt_label: String(
          trigger.dataset?.aiPromptLabel || trigger.textContent || '',
        ).trim(),
        prompt_type: resolvePromptType(trigger),
      };
      if (trigger.dataset?.aiChatAction === 'prompt' && trigger.dataset?.aiPromptId) {
        payload.prompt_id = String(trigger.dataset.aiPromptId);
      }
      return analyticsClient.pushChatInteraction(payload);
    }

    function syncHeaderMode() {
      navigationController.syncHeaderMode();
    }

    function prepareConversationView() {
      navigationController.prepareConversationView();
    }

    async function sendMessage(message) {
      await conversationController.send(message);
    }

    function showChatView(showOptions = {}) {
      return navigationController.showChat(showOptions);
    }

    function handleBack(event) {
      if (dealerPricingController && dealerPricingController.ownsView(stateManager.currentView)) {
        if (event) event.preventDefault();
        dealerPricingController.back();
        return true;
      }
      return navigationController.handleBack(event);
    }

    function renderPricingNameView(renderOptions = {}) {
      if (dealerPricingController) {
        conversationController.cancel();
        controlsController.setDisabled(false);
        return dealerPricingController.open();
      }
      return pricingFlowController.renderPricingName(renderOptions);
    }

    function handleViewQuote() {
      return pricingFlowController.handleViewQuote();
    }

    function handleSuggestion(button) {
      const buttonText = resolveSuggestionMessage(button);
      pushPromptInteraction(button);

      if (button.dataset.aiChatAction === 'pricing') {
        renderPricingNameView();
        return;
      }
      if (
        button.dataset.aiChatAction === 'hubspot' ||
        button.classList.contains('expert-chat-trigger')
      ) {
        hubSpotFormController.show();
        return;
      }
      if (button.dataset.aiChatAction === 'focus-input') {
        stateManager.setView(views.FREEFORM_PROMPT);
        const assistantMessage = String(
          button.dataset.aiAssistantMessage || '',
        ).trim();
        if (suggestionContainer) suggestionContainer.style.display = 'none';
        if (backButton) backButton.style.display = 'flex';
        messageController.clear();
        if (assistantMessage) {
          messageController.append(
            messageTypes.ASSISTANT,
            assistantMessage,
            messageFormats.TEXT,
          );
        }
        if (input) {
          input.placeholder = assistantMessage || defaultInputPlaceholder;
        }
        syncHeaderMode();
        requestFrame(() => {
          if (!input) return;
          input.focus();
          const cursorPosition = input.value.length;
          input.setSelectionRange(cursorPosition, cursorPosition);
        });
        return;
      }

      if (suggestionContainer) suggestionContainer.style.display = 'none';
      if (backButton) backButton.style.display = 'flex';
      syncHeaderMode();
      sendMessage(buttonText);
    }

    function destroy() {
      if (destroyed) return false;
      destroyed = true;
      if (backButton && backButtonBound) {
        backButton.removeEventListener('click', handleBack);
        backButtonBound = false;
      }
      if (conversationController) conversationController.cancel();
      if (pricingFlowController) pricingFlowController.destroy();
      if (dealerPricingController) dealerPricingController.destroy();
      if (hubSpotFormController) hubSpotFormController.destroy();
      if (controlsController) controlsController.destroy();
      if (widgetLifecycleController) widgetLifecycleController.destroy();
      return true;
    }

    const stateManager = createStateManager();
    const messageController = createMessageController({ container: chatBox });

    try {
      analyticsClient = createAnalytics({ window: windowRef });
      const chatClient = createChatClient();
      const hubSpotClient = createHubSpotClient();
      const inventoryIqClient = createInventoryIqClient();
      widgetLifecycleController = createWidgetLifecycle({
        document: chatRoot.ownerDocument,
        root: chatRoot,
        window: windowRef,
        onChat: () => showChatView(),
        onContactSales: (showOptions) => hubSpotFormController.show(showOptions),
        onPricing: () => renderPricingNameView(),
        onPromptInteraction: pushPromptInteraction,
      });
      const isDesktopFormOnly = widgetLifecycleController.isDesktopFormOnly;
      controlsController = createControlsController({
        root: chatRoot,
        input,
        sendButton,
        suggestionContainer,
        viewQuoteButton,
        window: windowRef,
        onInput: resizeMainInputTextarea,
        onInputReset: () => resizeMainInputTextarea({ reset: true }),
        onSend: sendMessage,
        onSuggestion: handleSuggestion,
        onViewQuote: handleViewQuote,
      });
      conversationController = createConversationController({
        client: chatClient,
        messages: messageController,
        stockNumber,
        storeCode,
        getMessageContext: enableGa4Context
          ? () => analyticsClient.getGa4MessageContext()
          : null,
        onBeforeSend: prepareConversationView,
        onControlsDisabled: controlsController.setDisabled,
      });
      navigationController = createNavigationController({
        root: chatRoot,
        stateManager,
        messages: messageController,
        conversation: conversationController,
        controls: controlsController,
        captureViewData: captureStructuredViewData,
        restoreStructuredView,
        resolveContinueDisabled: resolveRestoredContinueDisabled,
        clearFormErrors: () => {
          if (hubSpotFormController) hubSpotFormController.clearErrors();
        },
        resizeInput: resizeMainInputTextarea,
        isDesktopFormOnly,
        defaultInputPlaceholder,
      });
      hubSpotFormController = createHubSpotFormController({
        root: chatRoot,
        client: hubSpotClient,
        analytics: analyticsClient,
        navigation: navigationController,
        conversation: conversationController,
        messages: messageController,
        resizeInput: resizeMainInputTextarea,
        isDesktopFormOnly,
        onReturnToPdp: () => {
          if (Number(windowRef.innerWidth) > 1024) return false;
          return widgetLifecycleController.close();
        },
        defaultInputPlaceholder,
        stockNumber,
        productTitle,
        boatBrand,
        boatModel,
      });
      hubSpotFormController.initialize();
      quoteController = createQuoteController({
        root: chatRoot,
        stateManager,
        client: inventoryIqClient,
        navigation: navigationController,
        sendMessage,
        showChat: showChatView,
        stockNumber,
        storeCode,
        productTitle,
        marineMaxLogoUrl: chatRoot.dataset.quoteMarinemaxLogoUrl,
        poweredByLogoUrl: chatRoot.dataset.quotePoweredByLogoUrl,
      });
      pricingFlowController = createPricingFlowController({
        root: chatRoot,
        stateManager,
        client: inventoryIqClient,
        navigation: navigationController,
        sendMessage,
        createQuote: quoteController.create,
        renderQuote: quoteController.render,
        sendQuoteEmail: quoteController.sendEmail,
        showDesktopForm: () =>
          hubSpotFormController.show({ savePreviousState: false }),
        isDesktopFormOnly,
        resizeInput: resizeMainInputTextarea,
        defaultInputPlaceholder,
        stockNumber,
      });
      const dealerPricingModule = modules.dealerPricing || dependencies.dealerPricing;
      if (chatRoot.querySelector('[data-dealer-pricing]')) {
        dealerPricingController = requireFactory(dealerPricingModule, 'createDealerPricingController')({
          onEligibilityChange: () => controlsController.syncSuggestions(),
          root: chatRoot,
          window: windowRef,
          navigation: navigationController,
          stateManager,
          view: views.DEALER_PRICING,
        });
      }
      widgetLifecycleController.initialize();
      syncHeaderMode();
      resizeMainInputTextarea();
      if (backButton) {
        backButton.addEventListener('click', handleBack);
        backButtonBound = true;
      }
    } catch (error) {
      destroy();
      throw error;
    }

    return Object.freeze({
      destroy,
      root: chatRoot,
    });
  }

  function collectChatRoots(scope) {
    if (!scope) return [];
    const roots = [];
    if (typeof scope.matches === 'function' && scope.matches(chatRootSelector)) {
      roots.push(scope);
    }
    if (typeof scope.querySelectorAll === 'function') {
      scope.querySelectorAll(chatRootSelector).forEach((chatRoot) => {
        if (!roots.includes(chatRoot)) roots.push(chatRoot);
      });
    }
    return roots;
  }

  function initializeChatRoot(chatRoot, options = {}) {
    if (!chatRoot || instanceRegistry.has(chatRoot)) return false;
    const windowRef = options.window || root;
    const documentRef = options.document || chatRoot.ownerDocument;
    const lifecycleModule = (options.modules || dependencies).lifecycle;
    const initializeChatInstance = requireFactory(
      lifecycleModule,
      'initializeChatInstance',
    );
    const onError = createInitializationErrorHandler(windowRef, options.onError);
    return initializeChatInstance({
      document: documentRef,
      window: windowRef,
      instanceId: chatRoot.id,
      onError,
      initialize(instanceRoot) {
        const app = createChatApp({
          root: instanceRoot,
          window: windowRef,
          modules: options.modules,
        });
        instanceRegistry.set(instanceRoot, app);
        if (
          typeof instanceRoot.dispatchEvent === 'function' &&
          typeof windowRef.CustomEvent === 'function'
        ) {
          instanceRoot.dispatchEvent(
            new windowRef.CustomEvent('ai-chat:ready', { bubbles: true }),
          );
        }
      },
    });
  }

  function initializeScope(scope, options = {}) {
    return collectChatRoots(scope).filter((chatRoot) =>
      initializeChatRoot(chatRoot, options),
    ).length;
  }

  function destroyChatRoot(chatRoot) {
    const app = instanceRegistry.get(chatRoot);
    if (!app) return false;
    app.destroy();
    instanceRegistry.delete(chatRoot);
    delete chatRoot.dataset.aiChatInitialized;
    chatRoot.classList.remove('ai-chat-ready');
    chatRoot.ariaBusy = 'true';
    chatRoot.closest('.section-ai-chat-widget')?.classList.remove('ai-chat-ready');
    return true;
  }

  function destroyScope(scope) {
    return collectChatRoots(scope).filter(destroyChatRoot).length;
  }

  function containsExperimentConfig(scope) {
    if (!scope) return false;
    const selector = '[data-ai-chat-experiment-config]';
    return Boolean(
      (typeof scope.matches === 'function' && scope.matches(selector)) ||
        (typeof scope.querySelector === 'function' &&
          scope.querySelector(selector)),
    );
  }

  function refreshFloatingChat(documentRef, loadedRoots, options) {
    collectChatRoots(documentRef)
      .filter(
        (chatRoot) =>
          chatRoot.dataset.layoutMode === 'right_rail' &&
          !loadedRoots.includes(chatRoot),
      )
      .forEach((chatRoot) => {
        destroyChatRoot(chatRoot);
        initializeChatRoot(chatRoot, options);
      });
  }

  function bootstrap(options = {}) {
    const windowRef = options.window || root;
    const documentRef =
      options.document || (windowRef && windowRef.document) || null;
    if (!windowRef || !documentRef) {
      throw new Error('AI Chat bootstrap requires a document and window');
    }
    if (bootstrapRegistry.has(documentRef)) {
      return bootstrapRegistry.get(documentRef);
    }

    const instanceOptions = {
      document: documentRef,
      modules: options.modules,
      onError: options.onError,
      window: windowRef,
    };
    let readyListenerBound = false;

    function initializeDocument() {
      readyListenerBound = false;
      return initializeScope(documentRef, instanceOptions);
    }

    function handleSectionLoad(event) {
      const scope = event.target || documentRef;
      const loadedRoots = collectChatRoots(scope);
      initializeScope(scope, instanceOptions);
      const inlineLoaded = loadedRoots.some(
        (chatRoot) => chatRoot.dataset.layoutMode === 'inline',
      );
      if (inlineLoaded || containsExperimentConfig(scope)) {
        refreshFloatingChat(documentRef, loadedRoots, instanceOptions);
      }
    }

    function handleSectionUnload(event) {
      destroyScope(event.target || documentRef);
    }

    function destroyBootstrap() {
      if (readyListenerBound) {
        documentRef.removeEventListener('DOMContentLoaded', initializeDocument);
        readyListenerBound = false;
      }
      documentRef.removeEventListener('shopify:section:load', handleSectionLoad);
      documentRef.removeEventListener(
        'shopify:section:unload',
        handleSectionUnload,
      );
      destroyScope(documentRef);
      bootstrapRegistry.delete(documentRef);
    }

    const controller = Object.freeze({
      destroy: destroyBootstrap,
      initialize: initializeDocument,
    });
    bootstrapRegistry.set(documentRef, controller);
    documentRef.addEventListener('shopify:section:load', handleSectionLoad);
    documentRef.addEventListener('shopify:section:unload', handleSectionUnload);
    if (documentRef.readyState === 'loading') {
      readyListenerBound = true;
      documentRef.addEventListener('DOMContentLoaded', initializeDocument, {
        once: true,
      });
    } else {
      initializeDocument();
    }
    return controller;
  }

  const publicApi = Object.freeze({
    bootstrap,
    collectChatRoots,
    createChatApp,
    destroyChatRoot,
    destroyScope,
    initializeChatRoot,
    initializeScope,
    normalizeStockNumber,
    resolvePromptType,
  });

  if (root && root.document) bootstrap({ document: root.document, window: root });

  return publicApi;
});
